import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { UpdateEventDate } from '../api/api';
import { DecryptData } from '../config/common';

function EditEventDateModal({ show, handleClose, eventData, onSuccess }) {
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);

    // Format Date to YYYY-MM-DDTHH:mm for datetime-local input
    const toInputDatetime = (dateVal) => {
        if (!dateVal) return '';
        const d = new Date(dateVal);
        if (isNaN(d.getTime())) return '';
        const pad = (n) => String(n).padStart(2, '0');
        const year = d.getFullYear();
        const month = pad(d.getMonth() + 1);
        const day = pad(d.getDate());
        const hours = pad(d.getHours());
        const minutes = pad(d.getMinutes());
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    useEffect(() => {
        if (eventData?.endDate) {
            setEndDate(toInputDatetime(eventData.endDate));
        } else {
            // Default 7 days from now
            const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
            setEndDate(toInputDatetime(d));
        }
    }, [eventData, show]);

    const handleQuickAddDays = (days) => {
        const base = (endDate ? new Date(endDate) : new Date());
        const baseTime = !isNaN(base.getTime()) && base > new Date() ? base.getTime() : Date.now();
        const newDate = new Date(baseTime + days * 24 * 60 * 60 * 1000);
        setEndDate(toInputDatetime(newDate));
    };

    // Calculate predicted status
    const getPredictedStatus = () => {
        if (!endDate) return null;
        const target = new Date(endDate);
        if (isNaN(target.getTime())) return null;
        const now = new Date();
        const start = eventData?.startDate ? new Date(eventData.startDate) : now;

        if (target <= now) {
            return { label: 'Finished (Ended)', color: '#ef4444' };
        } else if (start > now) {
            return { label: 'Coming Soon', color: '#f59e0b' };
        } else {
            return { label: 'Started (Active / In Progress)', color: '#22c55e' };
        }
    };

    const predicted = getPredictedStatus();

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!eventData?._id) {
            toast.error('Event data not available');
            return;
        }
        if (!endDate) {
            toast.error('Please pick an end date');
            return;
        }

        const parsedEnd = new Date(endDate);
        if (isNaN(parsedEnd.getTime())) {
            toast.error('Invalid date format');
            return;
        }

        if (eventData?.startDate && parsedEnd <= new Date(eventData.startDate)) {
            toast.error('End Date must be after Start Date');
            return;
        }

        try {
            setLoading(true);
            const resp = await UpdateEventDate({
                eventId: eventData._id,
                newEndDate: parsedEnd.toISOString()
            });

            const data = (resp && typeof resp === 'string') ? DecryptData(resp) : resp;
            if (data?.status) {
                toast.success(data?.message || 'Event end date updated successfully');
                if (onSuccess) {
                    onSuccess(data?.data || { ...eventData, endDate: parsedEnd.toISOString() });
                }
                handleClose();
            } else {
                toast.error(data?.message || 'Failed to update event end date');
            }
        } catch (err) {
            console.error('Update event date error:', err);
            toast.error('An error occurred while updating end date');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            centered
            className='cmn_modal'
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
        >
            <Modal.Body style={{ background: '#181818', color: '#fff', borderRadius: '12px', border: '1px solid #333' }}>
                <div className='cmn_modal_header d-flex justify-content-between align-items-center pb-2 border-bottom border-secondary'>
                    <p className='cmn_modal_title mb-0' style={{ fontSize: '18px', fontWeight: 600 }}>
                        <i className="fa-regular fa-calendar-days me-2 text-warning"></i>
                        Edit Event End Date
                    </p>
                    <button className='cmn_modal_closer rounded-5' onClick={handleClose}>
                        <i className="fa-solid fa-xmark" />
                    </button>
                </div>

                <div className='mt-3'>
                    <div className='p-2 mb-3' style={{ background: '#222', borderRadius: '8px', border: '1px solid #333' }}>
                        <div className='text-muted small'>Event Title</div>
                        <div className='fw-bold text-white'>{eventData?.eventTitle || 'Untitled Event'}</div>
                        <div className='d-flex flex-wrap gap-3 mt-2 text-muted' style={{ fontSize: '12px' }}>
                            <div>
                                <span className='text-secondary'>Start Date: </span>
                                <span className='text-white'>
                                    {eventData?.startDate ? new Date(eventData.startDate).toLocaleString() : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className='text-secondary'>Current End: </span>
                                <span className='text-warning'>
                                    {eventData?.endDate ? new Date(eventData.endDate).toLocaleString() : 'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className='mb-3'>
                        <label className='form-label text-white small fw-bold mb-1'>Quick Extend Presets:</label>
                        <div className='d-flex flex-wrap gap-2'>
                            {[
                                { label: '+1 Day', days: 1 },
                                { label: '+3 Days', days: 3 },
                                { label: '+7 Days', days: 7 },
                                { label: '+14 Days', days: 14 },
                                { label: '+30 Days', days: 30 }
                            ].map((preset) => (
                                <button
                                    key={preset.label}
                                    type='button'
                                    onClick={() => handleQuickAddDays(preset.days)}
                                    className='btn btn-sm'
                                    style={{
                                        background: '#2a2a2a',
                                        color: '#f8be2d',
                                        border: '1px solid #444',
                                        borderRadius: '20px',
                                        padding: '3px 12px',
                                        fontSize: '12px'
                                    }}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className='mb-3'>
                        <label className='form-label text-white small fw-bold mb-1'>New End Date & Time:</label>
                        <input
                            type='datetime-local'
                            className='form-control'
                            style={{
                                background: '#101010',
                                color: '#fff',
                                border: '1px solid #444',
                                borderRadius: '8px',
                                padding: '10px'
                            }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>

                    {predicted && (
                        <div
                            className='p-2 mb-3 d-flex align-items-center justify-content-between'
                            style={{
                                background: '#202020',
                                borderRadius: '8px',
                                borderLeft: `4px solid ${predicted.color}`
                            }}
                        >
                            <span className='small text-muted'>Predicted Status after update:</span>
                            <span className='badge' style={{ background: predicted.color, color: '#fff' }}>
                                {predicted.label}
                            </span>
                        </div>
                    )}

                    <div className='d-flex justify-content-end gap-2 pt-2 border-top border-secondary'>
                        <button
                            type='button'
                            className='btn btn-secondary btn-sm px-3'
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type='button'
                            className='btn btn-warning btn-sm px-3 fw-bold'
                            style={{ background: '#f8be2d', color: '#000', border: 'none' }}
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : 'Update End Date'}
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default EditEventDateModal;
